'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';
import dotIcon from '@/public/icons/dot.svg';
import Link from 'next/link';
import Select from '@/ui/select';
import routes from '@/utils/routes';
import styles from './styles.module.css';
import useAuthStore from '@/store/auth';
import cl from 'clsx';

export default function Navigation({ dict }) {
    const { isAuth } = useAuthStore();
    const { tools_list } = dict.select;
    const router = useRouter();
    const pathname = usePathname(); // Get the current path
    const [scrollTarget, setScrollTarget] = useState(null);

    useEffect(() => {
        if (scrollTarget) {
            setTimeout(() => {
                const targetElement = document.getElementById(scrollTarget.link);
                if (targetElement) {
                    const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY + (scrollTarget.offset || 0);
                    smoothScrollTo(targetPosition);
                }
                setScrollTarget(null);
            }, 300);
        }
    }, [pathname]); // Perform a scroll after changing the route

    const toolsList = [
        { id: 1, title: tools_list.pa_tool },
        { id: 2, title: tools_list.sa_tool },
        { id: 3, title: tools_list.lca_tool }
    ];

    const navigation = [
        { id: 1, title: dict.header.home, link: routes.main },
        { id: 2, title: dict.header.idea, link: 'idea', offset: 120 },
        { id: 3, title: dict.header.tools, link: 'discover-top', offset: 30 },
        { id: 4, title: dict.header.contacts, link: routes.contacts },
        { id: 5, title: dict.header.faq, link: routes.faq }
    ];

    // Smooth scrolling function
    const smoothScrollTo = (targetY, duration = 500) => {
        const startY = window.scrollY;
        const difference = targetY - startY;
        const startTime = performance.now();

        const step = (currentTime) => {
            const elapsedTime = currentTime - startTime;
            const progress = Math.min(elapsedTime / duration, 1);

            window.scrollTo(0, startY + difference * progress);

            if (progress < 1) {
                requestAnimationFrame(step);
            }
        };

        requestAnimationFrame(step);
    };

    const handleScroll = (event, link, offset) => {
        event.preventDefault();

        const targetElement = document.getElementById(link);
        if (targetElement) {
            const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY + (offset || 0);
            smoothScrollTo(targetPosition);
        } else {
            // If on another page, save the scroll target and move on
            setScrollTarget({ link, offset });
            router.push(routes.main);
        }
    };

    return (
        <ul className={styles.navigation}>
            {!isAuth ? (
                navigation.map(({ id, title, link, offset }) => (
                    <div key={id} className={styles.navigation__item}>
                        {link.includes("#") || link.includes("idea") || link.includes("discover-top") ? (
                            <a
                                href={`${routes.main}#${link}`}
                                className={styles.navigation__link}
                                onClick={(event) => handleScroll(event, link, offset)}
                            >
                                <li className={cl(styles.navigation__title, 'custom-underline')}>
                                    {title}
                                </li>
                            </a>
                        ) : (
                            <Link className={styles.navigation__link} href={link}>
                                <li className={cl(styles.navigation__title, 'custom-underline')}>{title}</li>
                            </Link>
                        )}

                        <div className={styles.navigation__dot_wrapper}>
                            <Image
                                className={cl({ [styles.navigation__dot_hidden]: id === 5 })}
                                src={dotIcon}
                                alt="dot"
                            />
                        </div>
                    </div>
                ))
            ) : (
                <>
                    <div className={styles.navigation__item}>
                        <Link className={styles.navigation__link} href={routes.main}>
                            <li className={cl(styles.navigation__title, 'custom-underline')}>
                                {dict.header.home}
                            </li>
                        </Link>
                        <div className={styles.navigation__dot_wrapper}>
                            <Image className={cl(styles.navigation__dot)} src={dotIcon} alt="dot" />
                        </div>
                    </div>

                    <div className={cl(styles.navigation__item, 'navigation__item_select_tools')}>
                        <Select options={toolsList} defaultText={dict.header.tools} type="nav" dict={dict} />
                        <div className={styles.navigation__dot_wrapper_select}>
                            <Image className={cl(styles.navigation__dot)} src={dotIcon} alt="dot" />
                        </div>
                    </div>

                    <div className={styles.navigation__item}>
                        <Link className={styles.navigation__link} href={routes.contacts}>
                            <li className={cl(styles.navigation__title, 'custom-underline')}>
                                {dict.header.contacts}
                            </li>
                        </Link>
                        <div className={styles.navigation__dot_wrapper}>
                            <Image className={cl(styles.navigation__dot)} src={dotIcon} alt="dot" />
                        </div>
                    </div>

                    <div className={styles.navigation__item}>
                        <Link className={styles.navigation__link} href={routes.faq}>
                            <li className={cl(styles.navigation__title, 'custom-underline')}>{dict.header.faq}</li>
                        </Link>
                        <div className={styles.navigation__dot_wrapper}>
                            <Image className={cl(styles.navigation__dot_hidden)} src={dotIcon} alt="dot" />
                        </div>
                    </div>
                </>
            )}
        </ul>
    );
}
